/**
 * Utility functions for handling multi-tenant subdomain support
 * Supports formats:
 * - <client>.<subdomain>.localhost - for localhost development
 * - <client>.<subdomain>.domainname.com - for AWS deployed environments
 *
 * The subdomain is configurable via VITE_APP_SUBDOMAIN environment variable
 * (defaults to 'policymanager' if not specified)
 */

export interface SubdomainInfo {
  client: string | null;
  isValid: boolean;
  environment:
    | "localhost"
    | "development"
    | "staging"
    | "production"
    | "unknown";
  subdomain: string;
  domain: string;
  region?: string;
}

/**
 * Gets the configured app subdomain from environment variables
 * @returns string - The subdomain to use (defaults to 'policymanager')
 */
function getAppSubdomain(): string {
  return import.meta.env.VITE_APP_SUBDOMAIN || "policyportal";
}

/**
 * Validates if URL follows the strict required format: <client>.<subdomain>.highbond.localhost
 * @param hostname - The hostname to validate (defaults to window.location.hostname)
 * @returns object with validation result and details for logging
 */
export function validateStrictUrlFormat(hostname?: string): {
  isValid: boolean;
  details: {
    hostname: string;
    expectedFormat: string;
    actualParts: string[];
    issues: string[];
  };
} {
  const currentHostname =
    hostname || (typeof window !== "undefined" ? window.location.hostname : "");
  const appSubdomain = getAppSubdomain();
  const expectedFormat = `<client>.${appSubdomain}.highbond.localhost`;

  const details = {
    hostname: currentHostname,
    expectedFormat,
    actualParts: currentHostname.split("."),
    issues: [] as string[],
  };

  if (!currentHostname) {
    details.issues.push("No hostname provided");
    return { isValid: false, details };
  }

  const hostParts = currentHostname.split(".");

  // Must be exactly 4 parts: <client>.<subdomain>.highbond.localhost
  if (hostParts.length !== 4) {
    details.issues.push(
      `URL must have exactly 4 parts, found ${hostParts.length}`
    );
    return { isValid: false, details };
  }

  // Must end with 'localhost'
  if (hostParts[3] !== "localhost") {
    details.issues.push(`Must end with 'localhost', found '${hostParts[3]}'`);
    return { isValid: false, details };
  }

  // Must have 'highbond' in third position
  if (hostParts[2] !== "highbond") {
    details.issues.push(
      `Third part must be 'highbond', found '${hostParts[2]}'`
    );
    return { isValid: false, details };
  }

  // Validate subdomain matches configuration
  const subdomain = hostParts[1];
  if (subdomain !== appSubdomain) {
    details.issues.push(
      `Subdomain must be '${appSubdomain}', found '${subdomain}'`
    );
    return { isValid: false, details };
  }

  // Validate client identifier
  const client = hostParts[0];
  if (!isValidClient(client)) {
    details.issues.push(
      `Invalid client identifier '${client}' (must be 2-50 alphanumeric characters, hyphens, or underscores)`
    );
    return { isValid: false, details };
  }

  return { isValid: true, details };
}

/** Keep in sync with policy-portal-service `tenantService.ts` `regionFromHostname`. */
function regionFromHostname(hostname: string): string | undefined {
  if (hostname.includes("-uk")) return "uk";
  if (hostname.includes("-eu")) return "eu";
  if (hostname.includes("-au")) return "au";
  return undefined;
}

/**
 * Detects environment and region from hostname patterns
 */
function detectEnvironmentFromHostname(hostname: string): {
  environment:
    | "localhost"
    | "development"
    | "staging"
    | "production"
    | "unknown";
  domain: string;
  region?: string;
} {
  // Localhost environment
  if (hostname.endsWith(".localhost")) {
    return {
      environment: "localhost",
      domain: "localhost",
    };
  }

  // Check for development environments
  // Helper function to check if hostname matches domain or its direct subdomains
  function isHostMatching(hostname: string, domain: string): boolean {
    return (
      hostname === domain ||
      hostname.endsWith("." + domain)
    );
  }

  if (
    isHostMatching(hostname, "highbond-s3.com") ||
    isHostMatching(hostname, "diligentoneplatform-dev.com")
  ) {
    return {
      environment: "development",
      domain: isHostMatching(hostname, "highbond-s3.com")
        ? "highbond-s3.com"
        : "diligentoneplatform-dev.com",
      region: regionFromHostname(hostname),
    };
  }

  // Check for staging environments
  if (
    isHostMatching(hostname, "highbond-s2.com") ||
    isHostMatching(hostname, "diligentoneplatform-preprod.com")
  ) {
    return {
      environment: "staging",
      domain: isHostMatching(hostname, "highbond-s2.com")
        ? "highbond-s2.com"
        : "diligentoneplatform-preprod.com",
    };
  }

  // Check for production environments with regions
  if (
    isHostMatching(hostname, "highbond.com") ||
    isHostMatching(hostname, "diligentoneplatform.com")
  ) {
    const region = regionFromHostname(hostname) ?? "us";
    const domain = isHostMatching(hostname, "highbond.com")
      ? "highbond.com"
      : "diligentoneplatform.com";

    return {
      environment: "production",
      domain,
      region,
    };
  }

  return {
    environment: "unknown",
    domain: "unknown",
  };
}

/**
 * Extracts client identifier from the current URL's subdomain
 * @param hostname - The hostname to parse (defaults to window.location.hostname)
 * @returns SubdomainInfo object with client information
 */
export function extractClientFromSubdomain(hostname?: string): SubdomainInfo {
  const currentHostname =
    hostname || (typeof window !== "undefined" ? window.location.hostname : "");
  const appSubdomain = getAppSubdomain();

  // Default return value for invalid scenarios
  const invalidInfo: SubdomainInfo = {
    client: null,
    isValid: false,
    environment: "unknown",
    subdomain: appSubdomain,
    domain: "unknown",
  };

  if (!currentHostname) {
    return invalidInfo;
  }

  // Detect environment and domain info
  const envInfo = detectEnvironmentFromHostname(currentHostname);
  const hostParts = currentHostname.split(".");

  // For localhost: <client>.<subdomain>.highbond.localhost
  if (envInfo.environment === "localhost") {
    if (
      hostParts.length === 4 &&
      hostParts[2] === "highbond" &&
      hostParts[3] === "localhost"
    ) {
      const client = hostParts[0];
      const subdomain = hostParts[1];

      if (subdomain === appSubdomain && isValidClient(client)) {
        return {
          client,
          isValid: true,
          environment: "localhost",
          subdomain,
          domain: envInfo.domain,
        };
      }
    }
    return invalidInfo;
  }

  // For AWS environments: <client>.<subdomain>.domain or <client>.<subdomain>-region.domain
  if (envInfo.environment !== "unknown") {
    // Find the subdomain in the hostname
    let subdomainIndex = -1;
    let foundSubdomain = "";

    // Check for subdomain with potential region suffix
    for (let i = 0; i < hostParts.length; i++) {
      const part = hostParts[i];
      if (part === appSubdomain) {
        subdomainIndex = i;
        foundSubdomain = part;
        break;
      }
      // Also check for subdomain with region suffix (e.g., "policyportal-uk")
      if (
        part.startsWith(appSubdomain + "-") &&
        (part.endsWith("-uk") || part.endsWith("-eu") || part.endsWith("-au"))
      ) {
        subdomainIndex = i;
        foundSubdomain = appSubdomain; // Still use the base subdomain
        break;
      }
    }

    if (subdomainIndex === 1 && hostParts.length >= 3) {
      const client = hostParts[0];

      if (isValidClient(client)) {
        return {
          client,
          isValid: true,
          environment: envInfo.environment,
          subdomain: foundSubdomain,
          domain: envInfo.domain,
          region: envInfo.region,
        };
      }
    }
  }

  // All other patterns are invalid
  return invalidInfo;
}

/**
 * Gets the current client identifier
 * @returns string | null - The client identifier or null if not in multi-tenant mode
 */
export function getCurrentClient(): string | null {
  const subdomainInfo = extractClientFromSubdomain();
  return subdomainInfo.client;
}

/**
 * Checks if the current URL has valid subdomain format
 * @returns boolean - True if URL follows required subdomain pattern
 */
export function hasValidSubdomain(): boolean {
  const subdomainInfo = extractClientFromSubdomain();
  return subdomainInfo.isValid;
}

/**
 * Gets the environment type based on hostname
 * @returns 'localhost' | 'development' | 'staging' | 'production' | 'unknown'
 */
export function getEnvironmentType():
  | "localhost"
  | "development"
  | "staging"
  | "production"
  | "unknown" {
  const subdomainInfo = extractClientFromSubdomain();
  return subdomainInfo.environment;
}

/**
 * Validates if a client identifier is valid
 * @param client - The client identifier to validate
 * @returns boolean - True if the client identifier is valid
 */
export function isValidClient(client: string | null): boolean {
  if (!client) return false;

  // Client should be alphanumeric with hyphens/underscores allowed
  const clientRegex = /^[a-zA-Z0-9_-]+$/;
  return clientRegex.test(client) && client.length >= 2 && client.length <= 50;
}

/**
 * Builds a URL with the current client subdomain
 * @param baseDomain - The base domain (e.g., 'policymanager.localhost' or 'policymanager.example.com')
 * @param protocol - The protocol to use (defaults to current protocol)
 * @returns string - The full URL with client subdomain
 * @throws Error if no valid client is found
 */
export function buildClientUrl(baseDomain: string, protocol?: string): string {
  const client = getCurrentClient();
  const appSubdomain = getAppSubdomain();
  const currentProtocol =
    protocol ||
    (typeof window !== "undefined" ? window.location.protocol : "https:");

  if (!client || !isValidClient(client)) {
    throw new Error(
      `Cannot build client URL: No valid client found in subdomain. Please access via <client>.${appSubdomain}.localhost or <client>.${appSubdomain}.domain.com`
    );
  }

  return `${currentProtocol}//${client}.${baseDomain}`;
}

/**
 * Gets the current app subdomain from configuration
 * @returns string - The configured subdomain
 */
export function getSubdomain(): string {
  return getAppSubdomain();
}

/**
 * Builds the expected hostname format for the current environment
 * @param client - The client identifier
 * @param baseDomain - The base domain (defaults to appropriate domain for environment)
 * @returns string - The full hostname
 */
export function buildExpectedHostname(
  client: string,
  baseDomain?: string
): string {
  const appSubdomain = getAppSubdomain();
  const defaultBaseDomain = baseDomain || `${appSubdomain}.localhost`;
  return `${client}.${defaultBaseDomain}`;
}

/**
 * Builds the expected API hostname format for the current environment
 * Frontend: <client>.<subdomain>.<domain>
 * API: <client>.<subdomain>-api.<domain> (without region)
 * API with region: <client>.<subdomain>-api-<region>.<domain>
 * @param client - The client identifier
 * @param baseDomain - The base domain (e.g., 'localhost', 'highbond.localhost')
 * @returns string - The full API hostname
 */
export function buildApiHostname(
  client: string,
  baseDomain?: string,
  hostname?: string
): string {
  const appSubdomain = getAppSubdomain();

  // Determine the base domain from current environment if not provided
  let actualBaseDomain = baseDomain;
  if (!actualBaseDomain) {
    const envInfo = getEnvironmentInfo();

    if (envInfo.environment === "localhost") {
      // For localhost, check if using highbond.localhost pattern
      if (typeof window !== "undefined") {
        const currentHostname = window.location.hostname;
        const hostParts = currentHostname.split(".");

        if (
          hostParts.length === 4 &&
          hostParts[2] === "highbond" &&
          hostParts[3] === "localhost"
        ) {
          actualBaseDomain = "highbond.localhost";
        } else {
          actualBaseDomain = "localhost";
        }
      } else {
        actualBaseDomain = "localhost";
      }
    } else {
      // For all other environments (dev, staging, prod), use the detected domain
      actualBaseDomain = envInfo.domain;
    }
  }

  // Get region info for URL construction. Optional hostname lets tests
  // (and callers) avoid reading jsdom's non-configurable window.location.
  const envInfo = getEnvironmentInfo(hostname);

  // Build API subdomain with region suffix if present
  let apiSubdomain = `${appSubdomain}-api`;
  if (envInfo.region && envInfo.region !== "us") {
    // For non-US regions, add region suffix: policyportal-api-eu
    apiSubdomain = `${appSubdomain}-api-${envInfo.region}`;
  }

  return `${client}.${apiSubdomain}.${actualBaseDomain}`;
}

/**
 * Gets the API subdomain pattern (adds -api suffix and region if applicable)
 * @returns string - The API subdomain (e.g., 'policymanager-api' or 'policymanager-api-eu')
 */
export function getApiSubdomain(): string {
  const appSubdomain = getAppSubdomain();
  const envInfo = getEnvironmentInfo();

  // Build API subdomain with region suffix if present
  let apiSubdomain = `${appSubdomain}-api`;
  if (envInfo.region && envInfo.region !== "us") {
    // For non-US regions, add region suffix: policyportal-api-eu
    apiSubdomain = `${appSubdomain}-api-${envInfo.region}`;
  }

  return apiSubdomain;
}

/**
 * Checks if the current environment is localhost (for mocking purposes)
 * @returns boolean - True if running on localhost
 */
export function isLocalhostEnvironment(): boolean {
  const environmentType = getEnvironmentType();
  return environmentType === "localhost";
}

/**
 * Gets detailed environment information
 * @returns object with environment details
 */
export function getEnvironmentInfo(hostname?: string): {
  environment:
    | "localhost"
    | "development"
    | "staging"
    | "production"
    | "unknown";
  domain: string;
  region?: string;
  shouldUseMocking: boolean;
} {
  const subdomainInfo = extractClientFromSubdomain(hostname);
  return {
    environment: subdomainInfo.environment,
    domain: subdomainInfo.domain,
    region: subdomainInfo.region,
    shouldUseMocking: subdomainInfo.environment === "localhost",
  };
}

/**
 * Debug helper to log current subdomain information
 */
export function debugSubdomainInfo(): void {
  if (typeof window === "undefined") {
    console.warn("debugSubdomainInfo: Not running in browser environment");
    return;
  }

  const info = extractClientFromSubdomain();
  const envInfo = getEnvironmentInfo();
  console.group("🏢 Subdomain Info");
  console.log("Hostname:", window.location.hostname);
  console.log("Client:", info.client);
  console.log("Is Valid:", info.isValid);
  console.log("Environment:", info.environment);
  console.log("Domain:", info.domain);
  console.log("Region:", info.region || "us");
  console.log("Should Use Mocking:", envInfo.shouldUseMocking);
  console.log("Valid Client:", isValidClient(info.client));
  console.groupEnd();
}
